import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import client from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { FeedPathTag } from "@/components/FeedPathTag";
import { VersionTag } from "@/components/VersionTag";
import { Pencil, Save, Database } from "lucide-react";

function Field({ label, feedPath, disabled, value, onChange, testId }) {
  return (
    <div>
      <Label className="data-label mb-1 flex items-center gap-1">
        {label}
        <FeedPathTag path={feedPath} />
      </Label>
      <Input
        data-testid={testId}
        disabled={disabled}
        value={value}
        placeholder="https://…"
        className="font-mono text-xs"
        onChange={onChange}
      />
    </div>
  );
}

// Backend source of truth for which fields live in which section is
// models.CustomData / DEALER_MANAGED_CUSTOM_DATA_FIELDS — mirror any change
// there. MHQ_MANAGED_FIELDS is empty today; new MHQ-managed fields get added
// here (and gated by `isMhq` below) as they're introduced.
const MHQ_MANAGED_FIELDS = [];
const DEALER_MANAGED_FIELDS = [
  ["job_postings_url", "dealership.customData.jobPostingsUrl",
    "Dealer.WebGenerator.Module.Modul.config.JobWidgetUrl (id 6)"],
  ["trade_in_url", "dealership.customData.tradeInUrl",
    "Dealer.WebGenerator.Module.Modul.config.gibUnsDeinenGebrauchtenPath (id 24)"],
];

/**
 * "Custom Data" card. Two owner sections — "MHQ-managed" and
 * "Dealer-managed" — both gated by the same `canManage` permission
 * (dealer.custom_data.edit); `isMhq` splits the field set further on top of
 * it, same idea as MainContactCard: MHQ-managed fields stay view-only for a
 * non-MHQ (dealer-side) caller, matching the backend, which silently drops
 * them for a non-MHQ caller (server.py update_custom_data). Owns its own
 * local edit state; commits via PATCH and bubbles the fresh dealer document
 * up via `onSaved`.
 */
export function CustomDataCard({ dealer, dealerId, onSaved, canManage = false, isMhq = false }) {
  const { t } = useI18n();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const start = () => {
    setDraft({ ...(dealer.custom_data || {}) });
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await client.patch(`/dealers/${dealerId}/custom-data`, draft);
      onSaved(data);
      setEditing(false);
      toast.success(t("dealership.saved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const v = (k) => (editing ? draft?.[k] ?? "" : dealer.custom_data?.[k] || "");

  return (
    <Card className="mb-6 p-6" data-testid="custom-data-card">
      <div className="mb-4 flex items-start justify-between">
        <div className="flex items-center gap-2">
          <Database className="h-4 w-4 text-muted-foreground" />
          <h2 className="flex items-center gap-1.5 text-lg font-semibold">{t("dealership.customData.title")} <VersionTag version={dealer.card_versions?.custom_data} testId="version-tag-custom_data" /></h2>
        </div>
        {!editing ? (
          canManage && (
            <Button data-testid="edit-custom-data-btn" variant="ghost" size="sm" className="gap-1.5" onClick={start}>
              <Pencil className="h-3.5 w-3.5" />
              {t("common.edit")}
            </Button>
          )
        ) : (
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
              {t("common.cancel")}
            </Button>
            <Button data-testid="save-custom-data-btn" size="sm" onClick={save} disabled={saving} className="gap-1.5">
              <Save className="h-3.5 w-3.5" />
              {t("common.save")}
            </Button>
          </div>
        )}
      </div>

      <div className="mb-6">
        <p className="data-label mb-3">{t("dealership.customData.mhqManaged")}</p>
        {MHQ_MANAGED_FIELDS.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("dealership.customData.noMhqFields")}</p>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {MHQ_MANAGED_FIELDS.map(([key, labelKey, feedPath]) => (
              <Field
                key={key}
                label={t(labelKey)}
                feedPath={feedPath}
                testId={`custom-data-${key}`}
                disabled={!editing || saving || !canManage || !isMhq}
                value={v(key)}
                onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
              />
            ))}
          </div>
        )}
      </div>

      <div className="border-t border-border pt-6">
        <p className="data-label mb-3">{t("dealership.customData.dealerManaged")}</p>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {DEALER_MANAGED_FIELDS.map(([key, labelKey, feedPath]) => (
            <Field
              key={key}
              label={t(labelKey)}
              feedPath={feedPath}
              testId={`custom-data-${key}`}
              disabled={!editing || saving || !canManage}
              value={v(key)}
              onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
            />
          ))}
        </div>
      </div>
    </Card>
  );
}
